import { notification } from "antd";
import { makeAutoObservable } from "mobx";

import { ClientAPI, ManagerAPI } from "@/api";
import { ClientUpdated } from "@/events";
import { ViewModel } from "@/hooks/use-view-model";
import Query from "@/models/query";
import { EventBusService, IntlService } from "@/services";
import { PermissionsStore, SelectorStore } from "@/stores";

import { Props } from "./Form";
import { ISchema } from "./schema";

export class FormState implements ViewModel<Props> {
  constructor(
    private eventBusService: EventBusService,
    private intlService: IntlService,
    public permissionsStore: PermissionsStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  clientQuery = new Query(ClientAPI.getById);

  get client() {
    return this.clientQuery.data!;
  }

  onViewMount = async ({ clientId, setClientName }: Props) => {
    await this.clientQuery.submit({ id: clientId });

    if (this.clientQuery.isFulfilled) {
      setClientName(this.client.name);
    }

    if (this.permissionsStore.has("SelectManager")) {
      this.managerSelectorStore.setSelectedId(
        this.client.managerId ?? undefined,
      );
      this.managerSelectorStore.setParameters({});
      this.managerSelectorStore.fetchItems();
    }
  };

  onViewUnmount = () => {
    this.clientQuery.reset();
  };

  managerSelectorStore = new SelectorStore({
    filterMethod: ManagerAPI.filter,
    getByIdMethod: ManagerAPI.getById,
    labelKey: "name",
  });

  updateQuery = new Query(ClientAPI.update);

  handleSubmit = async (values: ISchema) => {
    if (!this.client) {
      return;
    }

    await this.updateQuery.submit({
      id: this.client.id,
      managerId: this.managerSelectorStore.selectedId,
      shouldRemoveManager: this.permissionsStore.has("SelectManager")
        ? !this.managerSelectorStore.selectedId
          ? true
          : undefined
        : undefined,
      ...values,
    });

    if (!this.updateQuery.isFulfilled) {
      return;
    }

    notification.success({
      message: "",
      description: this.intlService.intl.formatMessage({
        defaultMessage: "The client has been successfully updated.",
      }),
    });

    this.eventBusService.publish(
      new ClientUpdated({ clientId: this.client.id }),
    );
  };
}
